import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { ServiceChangeRequestService } from 'src/app/core/services/service-change-request.service';
import { Subject, finalize, takeUntil } from 'rxjs';
import {
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  EIremboFormlyFieldTypes,
  IIrembogovBasicLabelKeyPair,
} from '@irembo-andela/irembogov3-common';
import { HttpErrorResponse } from '@angular/common/http';
import { JourneyV3Model } from '../../../../../core/models/journey.model';
import { EServiceChangeRequestToggleHeaders } from '../../../../../core/models/service-change-request-toggle-header-section.enum';
import { JsonEditorComponent, JsonEditorOptions } from '@maaxgr/ang-jsoneditor';

@Component({
  selector: 'irembogov-journey-v3-config',
  templateUrl: './journey-v3-config.component.html',
  styleUrls: ['./journey-v3-config.component.scss'],
})
export class JourneyV3ConfigComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() changeRequestId!: string;
  @Input() workflowData!: Record<string, unknown>;
  @Output() stepSubmitted = new EventEmitter<boolean>();
  @Output() loadingStateChange = new EventEmitter<boolean>();

  @ViewChild(JsonEditorComponent, { static: false })
  jsonEditor!: JsonEditorComponent;

  private destroy$ = new Subject<void>();

  form = new FormGroup({});
  model: JourneyV3Model = {
    triggerParentMappings: [],
    additionalApplicationSettings: {
      hideFromUserList: false,
      rfaOnParent: false,
    },
  };
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [];

  isLoading = false;
  isSaving = false;

  // Service loading properties
  loadingServices = false;
  loadingMoreServices = false;
  serviceOptions: any[] = [];
  dropdownOptions: any[] = [];
  currentServicePage = 0;
  servicePageSize = 20;
  hasMoreServices = true;
  allServicesLoaded = false;
  serviceSearchTerm = '';

  // Workflow states tracking
  serviceWorkflowStates: Map<string, string[]> = new Map();
  currentJourneyStates: string[] = [];
  currentJourneyEvents: string[] = [];
  loadingWorkflowStates = false;

  // JSON Editor properties
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;
  selectedOption: EServiceChangeRequestToggleHeaders =
    EServiceChangeRequestToggleHeaders.CODE_EDITOR; // Default to JSON editor
  jsonEditorValidationErrors: ValidationErrors[] = [];
  public editorOptions: JsonEditorOptions;

  headerOptions: IIrembogovBasicLabelKeyPair<EServiceChangeRequestToggleHeaders>[] =
    [
      { label: 'Wizard', key: EServiceChangeRequestToggleHeaders.WIZARD },
      {
        label: 'Code Editor',
        key: EServiceChangeRequestToggleHeaders.CODE_EDITOR,
      },
    ];

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.mode = 'code';
    this.editorOptions.modes = ['code'];
  }

  ngOnInit(): void {
    this.loadServices();
    this.buildFormFields();
    this.loadConfiguration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflowData'] && changes['workflowData'].currentValue) {
      this.extractEventsFromWorkflowData(changes['workflowData'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (this.jsonEditor) {
      this.jsonEditor.setMode('code');
    }
  }

  /**
   * Load workflow states for services that are pre-selected in the configuration.
   * This ensures child state dropdowns are populated even before user interaction.
   */
  private loadWorkflowStatesForPreselectedServices(): void {
    if (!this.model.triggerParentMappings?.length) {
      return;
    }

    // Extract unique child service codes from the model
    const preselectedServiceCodes = [
      ...new Set(
        this.model.triggerParentMappings.map(
          mapping => mapping.childServiceCode
        )
      ),
    ];

    // Attempt to load workflow states for each preselected service
    preselectedServiceCodes.forEach(serviceCode => {
      this.loadWorkflowStatesForService(serviceCode);
    });
  }

  /**
   * Load workflow states for a specific service code.
   * Handles case where service data might not be loaded yet by retrying.
   */
  private loadWorkflowStatesForService(
    serviceCode: string,
    retryCount = 0
  ): void {
    const maxRetries = 10; // Max 5 seconds (10 * 500ms)

    // Find service in dropdown options
    const service = this.dropdownOptions.find(
      s => s.serviceCode === serviceCode
    );

    if (service?.changeRequestId) {
      // Service found, load its workflow states
      this.loadServiceWorkflowStates(serviceCode, service.changeRequestId);
    } else if (retryCount < maxRetries) {
      // Service not loaded yet, retry after a short delay
      setTimeout(() => {
        this.loadWorkflowStatesForService(serviceCode, retryCount + 1);
      }, 500);
    }
  }

  private buildFormFields(): void {
    this.fields = [
      {
        fieldGroup: [
          {
            key: 'additionalApplicationSettings.hideFromUserList',
            type: 'checkbox',
            wrappers: ['form-field', 'hint-field-wrapper', 'field-wrapper'],
            props: {
              label: 'Hide from User List',
              nonConfigurable: true,
              description:
                "If checked, all Child Applications initiated from this journey will be hidden from the user's main application tracking list",
            },
          },
          {
            key: 'additionalApplicationSettings.rfaOnParent',
            type: 'checkbox',
            wrappers: ['form-field', 'hint-field-wrapper', 'field-wrapper'],
            props: {
              label: 'RFA on Parent',
              nonConfigurable: true,
              description:
                'Request for Action button should will shown on parent application instead of child',
            },
          },
          {
            key: 'triggerParentMappings',
            type: EIremboFormlyFieldTypes.customexpansionrepeater,
            props: {
              label: 'Trigger Parent Mappings',
              addText: 'Add Trigger Mapping',
              titleFieldName: 'childServiceCode',
              hideLabel: true,
              nonConfigurable: true,
            },
            fieldArray: {
              fieldGroup: [
                {
                  fieldGroup: [
                    {
                      key: 'childServiceCode',
                      type: 'customserviceselector',
                      wrappers: [
                        'form-field',
                        'hint-field-wrapper',
                        'field-wrapper',
                      ],
                      props: {
                        label: 'Child Service Code',
                        required: true,
                        placeholder: 'Select a service',
                        searchPlaceholder: 'Search services...',
                        noResultsText: 'No services found',
                        loadingText: 'Loading services...',
                        loadingMoreText: 'Loading more...',
                        nonConfigurable: true,
                        options: [],
                        onSearch: (searchTerm: string) =>
                          this.searchServices(searchTerm),
                        onLoadMore: () => this.loadMoreServices(),
                        hasMoreData: () =>
                          this.hasMoreServices && !this.allServicesLoaded,
                        isLoading: () => this.loadingServices,
                        isLoadingMore: () => this.loadingMoreServices,
                      },
                      hooks: {
                        onInit: (field: any) => {
                          const updateOptions = () => {
                            const options = this.dropdownOptions.map(opt => ({
                              label: `${opt.serviceCode} - ${opt.serviceName}`,
                              value: opt.serviceCode,
                            }));
                            if (field.props) {
                              field.props.options = options;
                              this.cdr.detectChanges();
                            }
                          };

                          // Initial update
                          updateOptions();

                          // Update when services are loaded
                          const interval = setInterval(() => {
                            if (this.dropdownOptions.length > 0) {
                              updateOptions();
                            }
                          }, 500);

                          // Watch for value changes to load workflow states
                          if (field.formControl) {
                            field.formControl.valueChanges
                              .pipe(takeUntil(this.destroy$))
                              .subscribe((serviceCode: string) => {
                                if (serviceCode) {
                                  const service = this.dropdownOptions.find(
                                    s => s.serviceCode === serviceCode
                                  );
                                  if (service?.changeRequestId) {
                                    this.loadServiceWorkflowStates(
                                      serviceCode,
                                      service.changeRequestId
                                    );
                                  }
                                }
                              });
                          }

                          // Cleanup on destroy
                          this.destroy$.subscribe(() => {
                            clearInterval(interval);
                          });
                        },
                      },
                    },
                    {
                      key: 'childState',
                      type: 'select',
                      wrappers: [
                        'form-field',
                        'hint-field-wrapper',
                        'field-wrapper',
                      ],
                      props: {
                        label: 'Child State',
                        placeholder: 'Select child state',
                        required: true,
                        options: [],
                        nonConfigurable: true,
                      },
                      hooks: {
                        onInit: (field: any) => {
                          // Get the childServiceCode field from the same fieldGroup
                          const serviceCodeField =
                            field.parent?.fieldGroup?.find(
                              (f: any) => f.key === 'childServiceCode'
                            );

                          const updateStateOptions = (serviceCode: string) => {
                            const states =
                              this.serviceWorkflowStates.get(serviceCode) || [];
                            const options = states.map(state => ({
                              label: state,
                              value: state,
                            }));
                            if (field.props) {
                              field.props.options = options;
                              this.cdr.detectChanges();
                            }
                          };

                          // Watch for service code changes
                          if (serviceCodeField?.formControl) {
                            serviceCodeField.formControl.valueChanges
                              .pipe(takeUntil(this.destroy$))
                              .subscribe((serviceCode: string) => {
                                if (serviceCode) {
                                  // Clear current value when service changes
                                  field.formControl?.setValue(null);

                                  // Wait a bit for workflow states to load
                                  setTimeout(() => {
                                    updateStateOptions(serviceCode);
                                  }, 500);
                                }
                              });
                          }

                          // Initial load if service is already selected
                          const currentServiceCode =
                            serviceCodeField?.formControl?.value;
                          if (currentServiceCode) {
                            // Poll for workflow states to be loaded (for pre-selected services)
                            const pollInterval = setInterval(() => {
                              const states =
                                this.serviceWorkflowStates.get(
                                  currentServiceCode
                                );
                              if (states && states.length > 0) {
                                updateStateOptions(currentServiceCode);
                                clearInterval(pollInterval);
                              }
                            }, 300);

                            // Stop polling after 6 seconds
                            setTimeout(() => clearInterval(pollInterval), 6000);

                            // Initial attempt (might already be loaded)
                            updateStateOptions(currentServiceCode);

                            // Cleanup on destroy
                            this.destroy$.subscribe(() => {
                              clearInterval(pollInterval);
                            });
                          }
                        },
                      },
                    },
                    {
                      key: 'parentEvent',
                      type: 'select',
                      wrappers: [
                        'form-field',
                        'hint-field-wrapper',
                        'field-wrapper',
                      ],
                      props: {
                        label: 'Parent Event',
                        placeholder: 'Select parent event',
                        required: true,
                        options: [],
                        nonConfigurable: true,
                      },
                      hooks: {
                        onInit: (field: any) => {
                          const updateEventOptions = () => {
                            const options = this.currentJourneyEvents.map(
                              event => ({
                                label: event,
                                value: event,
                              })
                            );
                            if (field.props) {
                              field.props.options = options;
                              this.cdr.detectChanges();
                            }
                          };

                          // Initial update
                          updateEventOptions();

                          // Poll for updates until states are loaded
                          const interval = setInterval(() => {
                            if (this.currentJourneyEvents.length > 0) {
                              updateEventOptions();
                              clearInterval(interval);
                            }
                          }, 500);

                          // Cleanup on destroy
                          this.destroy$.subscribe(() => {
                            clearInterval(interval);
                          });
                        },
                      },
                    },
                  ],
                },
              ],
            },
          },
        ],
      },
    ];
  }

  private loadConfiguration(): void {
    this.isLoading = true;
    this.loadingStateChange.emit(true);

    this.serviceChangeRequestService
      .getJourneyServiceMapping(this.changeRequestId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loadingStateChange.emit(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<any>) => {
          const data = response.data as JourneyV3Model;

          // Load existing configuration
          // Update properties instead of replacing object to maintain formly binding
          this.model.triggerParentMappings = data.triggerParentMappings || [];
          this.model.additionalApplicationSettings =
            data.additionalApplicationSettings || {
              hideFromUserList: false,
              rfaOnParent: false,
            };

          // Load workflow states for pre-selected services
          this.loadWorkflowStatesForPreselectedServices();

          // Update JSON editor with loaded data
          // Use timeout to ensure ViewChild is initialized
          setTimeout(() => {
            this.updateJsonEditorWithModel();
          }, 200);

          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          const errorMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to load journey V3 configuration'
            );

          this.toastService.show({
            body: errorMessage.message,
            type: 'error',
          });
        },
      });
  }

  async onSave(): Promise<void> {
    // Validate JSON editor if in code editor mode
    if (
      this.selectedOption === EServiceChangeRequestToggleHeaders.CODE_EDITOR
    ) {
      await this.validateJsonEditor();
      if (this.jsonEditorValidationErrors.length > 0) {
        this.toastService.show({
          body: 'Please fix JSON validation errors',
          type: 'error',
        });
        return;
      }
    } else {
      // Validate form if in wizard mode
      if (!this.form.valid) {
        this.toastService.show({
          body: 'Please fill in all required fields',
          type: 'warning',
        });
        return;
      }
    }

    this.isSaving = true;

    this.serviceChangeRequestService
      .updateJourneyServiceMapping(this.changeRequestId, this.model)
      .pipe(
        finalize(() => (this.isSaving = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.toastService.show({
            body: 'Journey V3 configuration saved successfully',
            type: 'success',
          });
          this.stepSubmitted.emit(true);
        },
        error: (error: HttpErrorResponse) => {
          const errorMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to save journey V3 configuration'
            );

          this.toastService.show({
            body: errorMessage.message,
            type: 'error',
          });
          this.stepSubmitted.emit(false);
        },
      });
  }

  loadServices(search?: string, append = false): void {
    if (this.loadingServices || this.loadingMoreServices) {
      return;
    }

    let searchTermToUse = search;

    if (search === undefined) {
      searchTermToUse = undefined;
      this.serviceSearchTerm = '';
      this.currentServicePage = 0;
      this.servicePageSize = 20;
      this.hasMoreServices = true;
      this.allServicesLoaded = false;
      append = false;
    }

    if (search !== this.serviceSearchTerm) {
      this.serviceSearchTerm = search || '';
      this.currentServicePage = 0;
      this.servicePageSize = 20;
      this.hasMoreServices = true;
      this.allServicesLoaded = false;
      append = false;
      searchTermToUse = search || '';
    }

    if (append) {
      searchTermToUse = this.serviceSearchTerm || undefined;
    }

    if (
      this.allServicesLoaded &&
      searchTermToUse === this.serviceSearchTerm &&
      append
    ) {
      return;
    }

    const pageToRequest = 0;
    let sizeToRequest = this.servicePageSize;

    if (append && this.hasMoreServices) {
      sizeToRequest = this.servicePageSize + this.dropdownOptions.length;
      this.loadingMoreServices = true;
    } else {
      this.loadingServices = true;
    }

    this.serviceChangeRequestService
      .getServicesWithPagination(
        pageToRequest,
        sizeToRequest,
        searchTermToUse || undefined
      )
      .pipe(
        finalize(() => {
          this.loadingServices = false;
          this.loadingMoreServices = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response && response.data) {
            const newServices = response.data.content.map((service: any) => ({
              id: service.id,
              serviceName: service.serviceName,
              changeRequestId: service.changeRequestId,
              serviceCode: service.serviceCode,
            }));

            this.dropdownOptions = newServices;
            this.servicePageSize = sizeToRequest;
            this.currentServicePage = 0;
            this.hasMoreServices = !response.data.last;
            this.allServicesLoaded = response.data.last;

            this.cdr.detectChanges();
          }
        },
        error: () => {
          // Silent fail on error
        },
      });
  }

  searchServices(searchTerm: string): void {
    if (
      searchTerm === null ||
      searchTerm === undefined ||
      searchTerm.trim() === ''
    ) {
      this.loadServices(undefined, false);
    } else {
      this.loadServices(searchTerm.trim(), false);
    }
  }

  loadMoreServices(): void {
    if (
      this.hasMoreServices &&
      !this.loadingMoreServices &&
      !this.allServicesLoaded &&
      !this.loadingServices
    ) {
      this.loadServices(this.serviceSearchTerm, true);
    }
  }

  /**
   * Extract events and states from the workflow data passed from parent component
   */
  extractEventsFromWorkflowData(workflowData: Record<string, unknown>): void {
    const endStates: string[] = [];
    const events: string[] = [];

    if (workflowData?.['workflow'] && Array.isArray(workflowData['workflow'])) {
      (workflowData['workflow'] as any[]).forEach((step: any) => {
        if (step.endStateOne?.stateCode) {
          endStates.push(step.endStateOne.stateCode);
        }
        if (step.endStateTwo?.stateCode) {
          endStates.push(step.endStateTwo.stateCode);
        }
        // Only push event if it exists and is not empty
        if (step.event && step.event.trim() !== '') {
          events.push(step.event);
        }
      });
    }

    this.currentJourneyStates = [...new Set(endStates)];
    this.currentJourneyEvents = [...new Set(events)];

    this.cdr.detectChanges();
  }

  loadServiceWorkflowStates(
    serviceCode: string,
    changeRequestId: string
  ): void {
    if (!changeRequestId || this.serviceWorkflowStates.has(serviceCode)) {
      return;
    }

    this.serviceChangeRequestService
      .getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
        changeRequestId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response && response.data) {
            const workflowData: any = response.data;
            const endStates: string[] = [];

            if (workflowData?.workflow) {
              workflowData.workflow.forEach((step: any) => {
                if (step.endStateOne?.stateCode) {
                  endStates.push(step.endStateOne.stateCode);
                }
                if (step.endStateTwo?.stateCode) {
                  endStates.push(step.endStateTwo.stateCode);
                }
              });
            }

            this.serviceWorkflowStates.set(serviceCode, [
              ...new Set(endStates),
            ]);
            this.cdr.detectChanges();
          }
        },
        error: () => {
          // Silent fail
        },
      });
  }

  onSelectOptionChange(option: EServiceChangeRequestToggleHeaders): void {
    this.selectedOption = option;

    if (option === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      // Wait for Angular to render the JSON editor component
      setTimeout(() => {
        if (this.jsonEditor) {
          this.jsonEditor.setMode('code');
          this.updateJourneyData(option);
        } else {
          // Retry after a longer delay
          setTimeout(() => {
            if (this.jsonEditor) {
              this.jsonEditor.setMode('code');
              this.updateJourneyData(option);
            }
          }, 200);
        }
      }, 0);
    } else {
      // Switching to Wizard - update immediately
      this.updateJourneyData(option);
    }
  }

  async validateJsonEditor(): Promise<void> {
    const editorJson: any = this.jsonEditor.getEditor();
    this.jsonEditorValidationErrors = await editorJson.validate();
    if (this.jsonEditorValidationErrors.length > 0) {
      return;
    }
    this.model = this.jsonEditor.get() as JourneyV3Model;
  }

  updateJourneyData(selectedOption: EServiceChangeRequestToggleHeaders): void {
    if (selectedOption === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      // Switching to JSON editor from Wizard
      // The model should already have the latest data from the formly form
      this.updateJsonEditorWithModel();
    } else {
      // Switching to Wizard from JSON editor
      // Get data from JSON editor and update model only if valid
      if (this.jsonEditor) {
        try {
          const journeyData = this.jsonEditor.get() as JourneyV3Model;

          // Only update model if we got valid data from JSON editor
          if (journeyData && typeof journeyData === 'object') {
            // Update model properties instead of replacing the object to maintain formly binding
            this.model.triggerParentMappings =
              journeyData.triggerParentMappings || [];
            this.model.additionalApplicationSettings =
              journeyData.additionalApplicationSettings || {
                hideFromUserList: false,
                rfaOnParent: false,
              };

            // Force formly to recognize the model changes
            if (this.options.updateInitialValue) {
              this.options.updateInitialValue();
            }
            this.cdr.detectChanges();
          }
        } catch (error) {
          console.error('Error getting JSON editor data:', error);
        }
      }
    }
  }

  /**
   * Helper method to update JSON editor with current model data
   */
  private updateJsonEditorWithModel(): void {
    if (this.jsonEditor && this.model) {
      try {
        // Create a clean copy of the model to avoid reference issues
        const modelCopy = {
          triggerParentMappings: this.model.triggerParentMappings || [],
          additionalApplicationSettings: this.model
            .additionalApplicationSettings || {
            hideFromUserList: false,
            rfaOnParent: false,
          },
        };

        this.jsonEditor.update(modelCopy as any);
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error updating JSON editor with model:', error);
      }
    }
  }
}
